import React, { useEffect, useState, useMemo } from 'react';
import { useGallery } from '../hooks/useGallery';
import { CONTRACT_ADDRESS, CLASS_OF_SERVICE, getAirport } from '../constants';

// Get attribute value from metadata
const getAttribute = (attributes, traitType) => {
  const attr = attributes?.find(a => a.trait_type === traitType);
  return attr?.value || '';
};

// Default filter state
const DEFAULT_FILTERS = {
  flightNumber: '',
  date: '',
  departure: '',
  arrival: '',
  aircraft: '',
  tailNumber: '',
  configuration: '',
  classOfService: '',
  hasPolarisLounge: false,
  hasUnitedClub: false,
};

// Filter Panel Component
const FilterPanel = ({ filters, setFilters, nfts, onClear }) => {
  // Extract unique values from NFTs for dropdown options
  const uniqueValues = useMemo(() => {
    const values = {
      aircraft: new Set(),
      tailNumbers: new Set(),
      configurations: new Set(),
      departures: new Set(),
      arrivals: new Set(),
      dates: new Set(),
    };

    nfts.forEach(nft => {
      const aircraft = getAttribute(nft.attributes, 'Aircraft');
      const tailNumber = getAttribute(nft.attributes, 'Tail Number');
      const configuration = getAttribute(nft.attributes, 'Seat Configuration');
      const origin = getAttribute(nft.attributes, 'Origin');
      const destination = getAttribute(nft.attributes, 'Destination');
      const date = getAttribute(nft.attributes, 'Date');

      if (aircraft) values.aircraft.add(aircraft);
      if (tailNumber) values.tailNumbers.add(tailNumber);
      if (configuration) values.configurations.add(configuration);
      if (origin) values.departures.add(origin);
      if (destination) values.arrivals.add(destination);
      if (date) values.dates.add(date);
    });

    return {
      aircraft: Array.from(values.aircraft).sort(),
      tailNumbers: Array.from(values.tailNumbers).sort(),
      configurations: Array.from(values.configurations).sort(),
      departures: Array.from(values.departures).sort(),
      arrivals: Array.from(values.arrivals).sort(),
      dates: Array.from(values.dates).sort().reverse(),
    };
  }, [nfts]);

  const inputStyle = {
    width: '100%',
    padding: '10px 12px',
    borderRadius: '8px',
    border: '1px solid rgba(255,255,255,0.12)',
    background: 'rgba(255,255,255,0.04)',
    color: '#ffffff',
    fontSize: '13px',
    outline: 'none',
  };

  const labelStyle = {
    display: 'block',
    fontSize: '10px',
    fontWeight: '600',
    color: 'rgba(255,255,255,0.5)',
    marginBottom: '6px',
    letterSpacing: '0.5px',
    textTransform: 'uppercase',
  };

  const activeFilterCount = Object.entries(filters).filter(([key, v]) => 
    v !== '' && v !== false
  ).length;

  return (
    <div style={{
      background: 'rgba(255,255,255,0.03)',
      borderRadius: '12px',
      border: '1px solid rgba(255,255,255,0.08)',
      padding: '20px',
      marginBottom: '24px',
    }}>
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '16px',
      }}>
        <div style={{
          fontSize: '14px',
          fontWeight: '600',
          color: '#ffffff',
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
        }}>
          <span>🔍</span>
          Filter Flights
          {activeFilterCount > 0 && (
            <span style={{
              background: '#0066FF',
              color: '#ffffff',
              fontSize: '11px',
              padding: '2px 8px',
              borderRadius: '10px',
              fontWeight: '600',
            }}>
              {activeFilterCount}
            </span>
          )}
        </div>
        {activeFilterCount > 0 && (
          <button
            onClick={onClear}
            style={{
              background: 'rgba(255,255,255,0.1)',
              border: 'none',
              borderRadius: '6px',
              padding: '6px 12px',
              color: 'rgba(255,255,255,0.7)',
              cursor: 'pointer',
              fontSize: '12px',
            }}
          >
            Clear All
          </button>
        )}
      </div>

      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))',
        gap: '16px',
      }}>
        {/* Flight Number */}
        <div>
          <label style={labelStyle}>Flight Number</label>
          <input
            type="text"
            value={filters.flightNumber}
            onChange={(e) => setFilters(f => ({ ...f, flightNumber: e.target.value }))}
            placeholder="e.g. UA 328"
            style={inputStyle}
          />
        </div>

        {/* Date */}
        <div>
          <label style={labelStyle}>Date</label>
          <select
            value={filters.date}
            onChange={(e) => setFilters(f => ({ ...f, date: e.target.value }))}
            style={inputStyle}
          >
            <option value="">All Dates</option>
            {uniqueValues.dates.map(date => (
              <option key={date} value={date}>{date}</option>
            ))}
          </select>
        </div>

        {/* Departure Airport */}
        <div>
          <label style={labelStyle}>Departure Airport</label>
          <select
            value={filters.departure}
            onChange={(e) => setFilters(f => ({ ...f, departure: e.target.value }))}
            style={inputStyle}
          >
            <option value="">All Airports</option>
            {uniqueValues.departures.map(dep => (
              <option key={dep} value={dep}>{dep}</option>
            ))}
          </select>
        </div>

        {/* Arrival Airport */}
        <div>
          <label style={labelStyle}>Arrival Airport</label>
          <select
            value={filters.arrival}
            onChange={(e) => setFilters(f => ({ ...f, arrival: e.target.value }))}
            style={inputStyle}
          >
            <option value="">All Airports</option>
            {uniqueValues.arrivals.map(arr => (
              <option key={arr} value={arr}>{arr}</option>
            ))}
          </select>
        </div>

        {/* Aircraft Type */}
        <div>
          <label style={labelStyle}>Aircraft Type</label>
          <select
            value={filters.aircraft}
            onChange={(e) => setFilters(f => ({ ...f, aircraft: e.target.value }))}
            style={inputStyle}
          >
            <option value="">All Aircraft</option>
            {uniqueValues.aircraft.map(aircraft => (
              <option key={aircraft} value={aircraft}>{aircraft}</option>
            ))}
          </select>
        </div>

        {/* Tail Number */}
        <div>
          <label style={labelStyle}>Tail Number</label>
          <select
            value={filters.tailNumber}
            onChange={(e) => setFilters(f => ({ ...f, tailNumber: e.target.value }))}
            style={inputStyle}
          >
            <option value="">All Tail Numbers</option>
            {uniqueValues.tailNumbers.map(tail => (
              <option key={tail} value={tail}>{tail}</option>
            ))}
          </select>
        </div>

        {/* Seat Configuration */}
        <div>
          <label style={labelStyle}>Seat Configuration</label>
          <select
            value={filters.configuration}
            onChange={(e) => setFilters(f => ({ ...f, configuration: e.target.value }))}
            style={inputStyle}
          >
            <option value="">All Configurations</option>
            {uniqueValues.configurations.map(config => (
              <option key={config} value={config}>{config}</option>
            ))}
          </select>
        </div>

        {/* Class of Service */}
        <div>
          <label style={labelStyle}>Class of Service</label>
          <select
            value={filters.classOfService}
            onChange={(e) => setFilters(f => ({ ...f, classOfService: e.target.value }))}
            style={inputStyle}
          >
            <option value="">All Classes</option>
            {CLASS_OF_SERVICE.map((cls) => (
              <option key={cls.value} value={cls.value}>
                {cls.value}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Lounge Filter */}
      <div style={{ marginTop: '16px' }}>
        <label style={{ ...labelStyle, marginBottom: '10px' }}>Lounge Add-ons</label>
        <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
          <label style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            cursor: 'pointer',
            padding: '8px 14px',
            borderRadius: '8px',
            border: `1px solid ${filters.hasPolarisLounge ? '#0033A0' : 'rgba(255,255,255,0.12)'}`,
            background: filters.hasPolarisLounge ? 'rgba(0, 51, 160, 0.2)' : 'rgba(255,255,255,0.04)',
            transition: 'all 0.2s ease',
          }}>
            <input
              type="checkbox"
              checked={filters.hasPolarisLounge}
              onChange={(e) => setFilters(f => ({ ...f, hasPolarisLounge: e.target.checked }))}
              style={{ display: 'none' }}
            />
            <span style={{
              width: '16px',
              height: '16px',
              borderRadius: '4px',
              border: `2px solid ${filters.hasPolarisLounge ? '#0033A0' : 'rgba(255,255,255,0.3)'}`,
              background: filters.hasPolarisLounge ? '#0033A0' : 'transparent',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}>
              {filters.hasPolarisLounge && (
                <svg width="10" height="10" viewBox="0 0 12 12" fill="none">
                  <path d="M2 6L5 9L10 3" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              )}
            </span>
            <span style={{ fontSize: '12px', color: '#ffffff', fontWeight: '500' }}>
              ✨ Polaris Lounge
            </span>
          </label>

          <label style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            cursor: 'pointer',
            padding: '8px 14px',
            borderRadius: '8px',
            border: `1px solid ${filters.hasUnitedClub ? '#0066FF' : 'rgba(255,255,255,0.12)'}`,
            background: filters.hasUnitedClub ? 'rgba(0, 102, 255, 0.2)' : 'rgba(255,255,255,0.04)',
            transition: 'all 0.2s ease',
          }}>
            <input
              type="checkbox"
              checked={filters.hasUnitedClub}
              onChange={(e) => setFilters(f => ({ ...f, hasUnitedClub: e.target.checked }))}
              style={{ display: 'none' }}
            />
            <span style={{
              width: '16px',
              height: '16px',
              borderRadius: '4px',
              border: `2px solid ${filters.hasUnitedClub ? '#0066FF' : 'rgba(255,255,255,0.3)'}`,
              background: filters.hasUnitedClub ? '#0066FF' : 'transparent',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}>
              {filters.hasUnitedClub && (
                <svg width="10" height="10" viewBox="0 0 12 12" fill="none">
                  <path d="M2 6L5 9L10 3" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              )}
            </span>
            <span style={{ fontSize: '12px', color: '#ffffff', fontWeight: '500' }}>
              🎫 United Club
            </span>
          </label>
        </div>
      </div>
    </div>
  );
};

// Share Menu Component
const ShareMenu = ({ nft, onClose, position = 'bottom' }) => {
  const [copied, setCopied] = useState(false);
  
  const flightNumber = getAttribute(nft.attributes, 'Flight Number');
  const origin = getAttribute(nft.attributes, 'Origin');
  const destination = getAttribute(nft.attributes, 'Destination');
  const date = getAttribute(nft.attributes, 'Date');
  const classOfService = getAttribute(nft.attributes, 'Class of Service');
  
  const openSeaUrl = `https://opensea.io/assets/base/${CONTRACT_ADDRESS}/${nft.tokenId}`;
  
  // Build tweet text
  const tweetText = `Just minted my ${flightNumber} flight from ${origin} to ${destination} as an NFT! ✈️

${classOfService} • ${date}

Check it out on @OpenSea 👇
${openSeaUrl}

#UnitedAirlines #NFT #Aviation #Web3`;

  const twitterUrl = `https://twitter.com/intent/tweet?text=${encodeURIComponent(tweetText)}`;
  
  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(openSeaUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const handleShare = (e, url) => {
    e.stopPropagation();
    window.open(url, '_blank', 'width=600,height=400');
  };

  const menuStyle = {
    position: 'absolute',
    [position === 'top' ? 'bottom' : 'top']: '100%',
    right: 0,
    marginTop: position === 'bottom' ? '8px' : 0,
    marginBottom: position === 'top' ? '8px' : 0,
    background: 'rgba(20, 20, 30, 0.98)',
    backdropFilter: 'blur(12px)',
    borderRadius: '12px',
    border: '1px solid rgba(255,255,255,0.1)',
    padding: '8px',
    minWidth: '180px',
    boxShadow: '0 10px 40px rgba(0,0,0,0.5)',
    zIndex: 100,
  };

  const buttonStyle = {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    width: '100%',
    padding: '10px 12px',
    background: 'transparent',
    border: 'none',
    borderRadius: '8px',
    color: '#ffffff',
    fontSize: '13px',
    fontWeight: '500',
    cursor: 'pointer',
    transition: 'background 0.2s',
    textAlign: 'left',
  };

  return (
    <div 
      style={menuStyle}
      onClick={(e) => e.stopPropagation()}
    >
      {/* Twitter/X */}
      <button
        onClick={(e) => handleShare(e, twitterUrl)}
        style={buttonStyle}
        onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(29, 161, 242, 0.2)'}
        onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
      >
        <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
          <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
        </svg>
        Share on X
      </button>

      {/* Copy Link */}
      <button
        onClick={(e) => {
          e.stopPropagation();
          handleCopyLink();
        }}
        style={buttonStyle}
        onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(255,255,255,0.1)'}
        onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
      >
        {copied ? (
          <>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#22C55E" strokeWidth="2">
              <path d="M20 6L9 17l-5-5"/>
            </svg>
            <span style={{ color: '#22C55E' }}>Copied!</span>
          </>
        ) : (
          <>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <rect x="9" y="9" width="13" height="13" rx="2" ry="2"/>
              <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/>
            </svg>
            Copy Link
          </>
        )}
      </button>

      {/* OpenSea */}
      <button
        onClick={(e) => {
          e.stopPropagation();
          window.open(openSeaUrl, '_blank');
        }}
        style={buttonStyle}
        onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(32, 129, 226, 0.2)'}
        onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
      >
        <svg width="18" height="18" viewBox="0 0 90 90" fill="#2081E2">
          <path d="M45 0C20.151 0 0 20.151 0 45C0 69.849 20.151 90 45 90C69.849 90 90 69.849 90 45C90 20.151 69.858 0 45 0ZM22.203 46.512L22.392 46.206L34.101 27.891C34.272 27.63 34.677 27.657 34.803 27.945C36.756 32.328 38.448 37.782 37.656 41.175C37.323 42.57 36.396 44.46 35.352 46.206C35.217 46.458 35.073 46.701 34.911 46.935C34.839 47.043 34.713 47.106 34.578 47.106H22.545C22.221 47.106 22.032 46.755 22.203 46.512ZM74.376 52.812C74.376 52.983 74.277 53.127 74.133 53.19C73.224 53.577 70.119 55.008 68.832 56.799C65.538 61.38 63.027 67.932 57.402 67.932H33.948C25.632 67.932 18.9 61.173 18.9 52.83V52.56C18.9 52.344 19.08 52.164 19.305 52.164H32.373C32.634 52.164 32.823 52.398 32.805 52.659C32.706 53.505 32.868 54.378 33.273 55.17C34.047 56.745 35.658 57.726 37.395 57.726H43.866V52.677H37.467C37.143 52.677 36.945 52.299 37.134 52.029C37.206 51.921 37.287 51.813 37.368 51.687C37.971 50.823 38.835 49.491 39.699 47.97C40.284 46.944 40.851 45.846 41.31 44.748C41.4 44.55 41.472 44.343 41.553 44.145C41.679 43.794 41.805 43.461 41.895 43.128C41.985 42.858 42.066 42.57 42.138 42.3C42.354 41.364 42.444 40.374 42.444 39.348C42.444 38.943 42.426 38.52 42.39 38.115C42.372 37.674 42.318 37.233 42.264 36.792C42.228 36.414 42.156 36.036 42.084 35.649C41.985 35.073 41.859 34.506 41.715 33.948L41.661 33.732C41.553 33.378 41.454 33.042 41.328 32.697C40.959 31.563 40.545 30.447 40.095 29.403C39.924 28.998 39.735 28.602 39.546 28.215C39.276 27.63 39.006 27.099 38.763 26.586C38.619 26.316 38.493 26.073 38.367 25.821C38.223 25.533 38.07 25.245 37.926 24.975C37.818 24.759 37.692 24.552 37.602 24.345L36.675 22.626C36.567 22.419 36.756 22.176 36.981 22.239L42.201 23.67H42.219C42.228 23.67 42.228 23.67 42.237 23.67L42.885 23.859L43.605 24.075L43.866 24.156V20.91C43.866 19.152 45.27 17.73 47.01 17.73C47.88 17.73 48.663 18.072 49.221 18.639C49.779 19.206 50.121 19.989 50.121 20.91V25.38L50.652 25.515C50.697 25.533 50.742 25.551 50.778 25.578C50.913 25.686 51.111 25.848 51.363 26.046C51.561 26.208 51.768 26.406 52.02 26.613C52.515 27.027 53.1 27.549 53.73 28.143C53.901 28.296 54.063 28.449 54.216 28.611C55.008 29.376 55.89 30.258 56.736 31.239C56.97 31.518 57.195 31.806 57.429 32.103C57.663 32.409 57.906 32.706 58.113 33.003C58.392 33.408 58.698 33.822 58.959 34.254C59.076 34.452 59.211 34.659 59.319 34.857C59.625 35.388 59.895 35.937 60.147 36.486C60.264 36.738 60.372 37.008 60.462 37.269C60.732 37.944 60.939 38.628 61.074 39.312C61.119 39.456 61.155 39.618 61.173 39.762V39.798C61.227 40.041 61.245 40.302 61.263 40.572C61.335 41.355 61.299 42.138 61.146 42.921C61.074 43.281 60.984 43.623 60.867 43.983C60.75 44.334 60.633 44.694 60.48 45.036C60.183 45.747 59.832 46.44 59.409 47.088C59.283 47.313 59.139 47.556 58.986 47.781C58.824 48.024 58.653 48.258 58.5 48.483C58.293 48.762 58.077 49.059 57.852 49.32C57.654 49.599 57.447 49.869 57.222 50.112C56.907 50.49 56.601 50.85 56.277 51.201C56.097 51.408 55.899 51.624 55.692 51.813C55.494 52.029 55.287 52.218 55.098 52.398C54.783 52.695 54.522 52.929 54.297 53.127L53.838 53.541C53.748 53.622 53.631 53.667 53.505 53.667H50.121V57.726H54.738C55.836 57.726 56.889 57.339 57.735 56.625C58.032 56.364 59.373 55.17 60.948 53.361C61.002 53.298 61.074 53.253 61.155 53.235L73.863 49.707C74.124 49.635 74.376 49.833 74.376 50.112V52.812Z"/>
        </svg>
        View on OpenSea
      </button>
    </div>
  );
};

// Share Button Component  
const ShareButton = ({ nft, size = 'small' }) => {
  const [showMenu, setShowMenu] = useState(false);
  
  const buttonSize = size === 'small' ? '32px' : '40px';
  const iconSize = size === 'small' ? '14' : '18';

  return (
    <div style={{ position: 'relative' }}>
      <button
        onClick={(e) => {
          e.stopPropagation();
          setShowMenu(!showMenu);
        }}
        style={{
          width: buttonSize,
          height: buttonSize,
          borderRadius: '8px',
          background: 'rgba(255,255,255,0.1)',
          border: 'none',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          transition: 'all 0.2s',
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.background = 'rgba(0, 102, 255, 0.3)';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.background = 'rgba(255,255,255,0.1)';
        }}
      >
        <svg width={iconSize} height={iconSize} viewBox="0 0 24 24" fill="none" stroke="#ffffff" strokeWidth="2">
          <circle cx="18" cy="5" r="3"/>
          <circle cx="6" cy="12" r="3"/>
          <circle cx="18" cy="19" r="3"/>
          <line x1="8.59" y1="13.51" x2="15.42" y2="17.49"/>
          <line x1="15.41" y1="6.51" x2="8.59" y2="10.49"/>
        </svg>
      </button>
      
      {showMenu && (
        <>
          {/* Backdrop to close menu */}
          <div 
            style={{
              position: 'fixed',
              inset: 0,
              zIndex: 99,
            }}
            onClick={(e) => {
              e.stopPropagation();
              setShowMenu(false);
            }}
          />
          <ShareMenu nft={nft} onClose={() => setShowMenu(false)} />
        </>
      )}
    </div>
  );
};

// Modal Share Section - inline share buttons for modal
const ModalShareSection = ({ nft }) => {
  const [copied, setCopied] = useState(false);
  
  const flightNumber = getAttribute(nft.attributes, 'Flight Number');
  const origin = getAttribute(nft.attributes, 'Origin');
  const destination = getAttribute(nft.attributes, 'Destination');
  const date = getAttribute(nft.attributes, 'Date');
  const classOfService = getAttribute(nft.attributes, 'Class of Service');
  
  const openSeaUrl = `https://opensea.io/assets/base/${CONTRACT_ADDRESS}/${nft.tokenId}`;
  
  const tweetText = `Just minted my ${flightNumber} flight from ${origin} to ${destination} as an NFT! ✈️

${classOfService} • ${date}

Check it out on @OpenSea 👇
${openSeaUrl}

#UnitedAirlines #NFT #Aviation #Web3`;

  const twitterUrl = `https://twitter.com/intent/tweet?text=${encodeURIComponent(tweetText)}`;
  
  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(openSeaUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const buttonStyle = {
    flex: 1,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
    padding: '12px',
    borderRadius: '8px',
    border: 'none',
    cursor: 'pointer',
    fontSize: '13px',
    fontWeight: '500',
    transition: 'all 0.2s',
  };

  return (
    <div style={{ marginBottom: '16px' }}>
      <div style={{
        fontSize: '10px',
        color: 'rgba(255,255,255,0.4)',
        textTransform: 'uppercase',
        letterSpacing: '0.5px',
        marginBottom: '10px',
      }}>
        Share
      </div>
      <div style={{ display: 'flex', gap: '10px' }}>
        {/* Twitter/X */}
        <button
          onClick={() => window.open(twitterUrl, '_blank', 'width=600,height=400')}
          style={{
            ...buttonStyle,
            background: 'rgba(29, 161, 242, 0.15)',
            color: '#1DA1F2',
          }}
          onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(29, 161, 242, 0.25)'}
          onMouseLeave={(e) => e.currentTarget.style.background = 'rgba(29, 161, 242, 0.15)'}
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
            <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
          </svg>
          Share on X
        </button>

        {/* Copy Link */}
        <button
          onClick={handleCopyLink}
          style={{
            ...buttonStyle,
            background: copied ? 'rgba(34, 197, 94, 0.15)' : 'rgba(255,255,255,0.05)',
            color: copied ? '#22C55E' : '#ffffff',
          }}
          onMouseEnter={(e) => {
            if (!copied) e.currentTarget.style.background = 'rgba(255,255,255,0.1)';
          }}
          onMouseLeave={(e) => {
            if (!copied) e.currentTarget.style.background = 'rgba(255,255,255,0.05)';
          }}
        >
          {copied ? (
            <>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M20 6L9 17l-5-5"/>
              </svg>
              Copied!
            </>
          ) : (
            <>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <rect x="9" y="9" width="13" height="13" rx="2" ry="2"/>
                <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/>
              </svg>
              Copy Link
            </>
          )}
        </button>
      </div>
    </div>
  );
};

// NFT Card Component
const NFTCard = ({ nft }) => {
  const [imageLoaded, setImageLoaded] = useState(false);
  const [imageError, setImageError] = useState(false);

  const classOfService = getAttribute(nft.attributes, 'Class of Service');
  const flightNumber = getAttribute(nft.attributes, 'Flight Number');
  const origin = getAttribute(nft.attributes, 'Origin');
  const destination = getAttribute(nft.attributes, 'Destination');
  const date = getAttribute(nft.attributes, 'Date');
  const hasPolarisLounge = getAttribute(nft.attributes, 'Polaris Lounge') === 'Yes';
  const hasUnitedClub = getAttribute(nft.attributes, 'United Club') === 'Yes';

  const openSeaUrl = `https://opensea.io/assets/base/${CONTRACT_ADDRESS}/${nft.tokenId}`;

  return (
    <div
      onClick={() => window.open(openSeaUrl, '_blank')}
      style={{
        background: 'rgba(255,255,255,0.03)',
        borderRadius: '12px',
        border: '1px solid rgba(255,255,255,0.08)',
        overflow: 'hidden',
        cursor: 'pointer',
        transition: 'all 0.2s ease',
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.transform = 'translateY(-4px)';
        e.currentTarget.style.borderColor = 'rgba(0, 102, 255, 0.3)';
        e.currentTarget.style.boxShadow = '0 12px 40px rgba(0, 102, 255, 0.15)';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.transform = 'translateY(0)';
        e.currentTarget.style.borderColor = 'rgba(255,255,255,0.08)';
        e.currentTarget.style.boxShadow = 'none';
      }}
    >
      {/* Image */}
      <div style={{
        aspectRatio: '600 / 380',
        background: 'rgba(0,0,0,0.3)',
        position: 'relative',
        overflow: 'hidden',
      }}>
        {!imageLoaded && !imageError && (
          <div style={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: 'rgba(255,255,255,0.3)',
            fontSize: '14px',
          }}>
            Loading...
          </div>
        )}
        {imageError && (
          <div style={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: 'rgba(255,255,255,0.3)',
            fontSize: '14px',
            flexDirection: 'column',
            gap: '8px',
          }}>
            <span style={{ fontSize: '24px' }}>✈️</span>
            <span>Image unavailable</span>
          </div>
        )}
        <img
          src={nft.imageUrl}
          alt={nft.name}
          onLoad={() => setImageLoaded(true)}
          onError={() => setImageError(true)}
          style={{
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            opacity: imageLoaded ? 1 : 0,
            transition: 'opacity 0.3s ease',
          }}
        />
        {/* Token ID badge and Share button */}
        <div style={{
          position: 'absolute',
          top: '8px',
          right: '8px',
          display: 'flex',
          gap: '6px',
          alignItems: 'center',
        }}>
          <ShareButton nft={nft} size="small" />
          <div style={{
            background: 'rgba(0,0,0,0.7)',
            backdropFilter: 'blur(4px)',
            padding: '4px 8px',
            borderRadius: '6px',
            fontSize: '11px',
            fontWeight: '600',
            color: 'rgba(255,255,255,0.8)',
          }}>
            #{nft.tokenId}
          </div>
        </div>

        {/* Lounge badges */}
        {(hasPolarisLounge || hasUnitedClub) && (
          <div style={{
            position: 'absolute',
            bottom: '8px',
            left: '8px',
            display: 'flex',
            gap: '4px',
          }}>
            {hasPolarisLounge && (
              <div style={{
                background: 'linear-gradient(135deg, #0033A0 0%, #004494 100%)',
                padding: '3px 8px',
                borderRadius: '4px',
                fontSize: '9px',
                fontWeight: '600',
                color: '#ffffff',
              }}>
                ✨ POLARIS
              </div>
            )}
            {hasUnitedClub && (
              <div style={{
                background: 'linear-gradient(135deg, #0066FF 0%, #0052CC 100%)',
                padding: '3px 8px',
                borderRadius: '4px',
                fontSize: '9px',
                fontWeight: '600',
                color: '#ffffff',
              }}>
                🎫 CLUB
              </div>
            )}
          </div>
        )}
      </div>

      {/* Info */}
      <div style={{ padding: '16px' }}>
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          marginBottom: '12px',
        }}>
          <div>
            <div style={{
              fontSize: '16px',
              fontWeight: '600',
              color: '#ffffff',
              marginBottom: '4px',
            }}>
              {flightNumber}
            </div>
            <div style={{
              fontSize: '20px',
              fontWeight: '700',
              color: '#0066FF',
            }}>
              {origin} → {destination}
            </div>
          </div>
        </div>

        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          paddingTop: '12px',
          borderTop: '1px solid rgba(255,255,255,0.08)',
        }}>
          <div style={{
            fontSize: '12px',
            color: 'rgba(255,255,255,0.5)',
          }}>
            {date}
          </div>
          <div style={{
            fontSize: '11px',
            fontWeight: '600',
            color: '#60A5FA',
            background: 'rgba(96, 165, 250, 0.1)',
            padding: '4px 8px',
            borderRadius: '6px',
          }}>
            {classOfService}
          </div>
        </div>
      </div>
    </div>
  );
};

// NFT Detail Modal
const NFTModal = ({ nft, onClose }) => {
  if (!nft) return null;

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.8)',
        backdropFilter: 'blur(8px)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
        padding: '20px',
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="modal-content"
        style={{
          background: 'linear-gradient(145deg, #0a0a0a 0%, #1a1a2e 50%, #0a0a0a 100%)',
          borderRadius: '16px',
          border: '1px solid rgba(255,255,255,0.1)',
          maxWidth: '700px',
          width: '100%',
          maxHeight: '90vh',
          overflow: 'auto',
        }}
      >
        {/* Image */}
        <img
          src={nft.imageUrl}
          alt={nft.name}
          style={{
            width: '100%',
            borderRadius: '16px 16px 0 0',
          }}
        />

        {/* Details */}
        <div style={{ padding: '24px' }}>
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            marginBottom: '20px',
          }}>
            <div>
              <h2 style={{
                fontSize: '20px',
                fontWeight: '700',
                color: '#ffffff',
                margin: '0 0 4px 0',
              }}>
                {nft.name}
              </h2>
              <div style={{
                fontSize: '13px',
                color: 'rgba(255,255,255,0.5)',
              }}>
                Token #{nft.tokenId}
              </div>
            </div>
            <button
              onClick={onClose}
              style={{
                background: 'rgba(255,255,255,0.1)',
                border: 'none',
                borderRadius: '8px',
                padding: '8px 12px',
                color: '#ffffff',
                cursor: 'pointer',
                fontSize: '14px',
              }}
            >
              ✕
            </button>
          </div>

          {/* Attributes grid */}
          <div className="modal-attributes" style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(3, 1fr)',
            gap: '12px',
            marginBottom: '20px',
          }}>
            {nft.attributes?.map((attr, i) => (
              <div
                key={i}
                style={{
                  background: 'rgba(255,255,255,0.03)',
                  borderRadius: '8px',
                  padding: '12px',
                  border: '1px solid rgba(255,255,255,0.06)',
                }}
              >
                <div style={{
                  fontSize: '10px',
                  color: 'rgba(255,255,255,0.4)',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px',
                  marginBottom: '4px',
                }}>
                  {attr.trait_type}
                </div>
                <div style={{
                  fontSize: '13px',
                  fontWeight: '600',
                  color: '#ffffff',
                  wordBreak: 'break-word',
                }}>
                  {attr.value}
                </div>
              </div>
            ))}
          </div>

          {/* Owner */}
          <div style={{
            background: 'rgba(255,255,255,0.03)',
            borderRadius: '8px',
            padding: '12px',
            marginBottom: '16px',
          }}>
            <div style={{
              fontSize: '10px',
              color: 'rgba(255,255,255,0.4)',
              textTransform: 'uppercase',
              letterSpacing: '0.5px',
              marginBottom: '4px',
            }}>
              Owner
            </div>
            <div style={{
              fontSize: '13px',
              fontFamily: 'monospace',
              color: '#ffffff',
            }}>
              {nft.owner}
            </div>
          </div>

          {/* Share Section */}
          <ModalShareSection nft={nft} />

          {/* Links */}
          <div style={{ display: 'flex', gap: '12px' }}>
            <a
              href={`https://opensea.io/assets/base/${CONTRACT_ADDRESS}/${nft.tokenId}`}
              target="_blank"
              rel="noopener noreferrer"
              style={{
                flex: 1,
                padding: '12px',
                background: 'rgba(255,255,255,0.05)',
                borderRadius: '8px',
                textAlign: 'center',
                color: '#60a5fa',
                textDecoration: 'none',
                fontSize: '13px',
                fontWeight: '500',
              }}
            >
              View on OpenSea →
            </a>
            <a
              href={nft.metadata?.external_url}
              target="_blank"
              rel="noopener noreferrer"
              style={{
                flex: 1,
                padding: '12px',
                background: 'rgba(255,255,255,0.05)',
                borderRadius: '8px',
                textAlign: 'center',
                color: '#60a5fa',
                textDecoration: 'none',
                fontSize: '13px',
                fontWeight: '500',
              }}
            >
              View on FlightAware →
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

// Haversine formula to calculate distance between two coordinates
const calculateDistance = (coords1, coords2) => {
  if (!coords1 || !coords2 || (coords1[0] === 0 && coords1[1] === 0) || (coords2[0] === 0 && coords2[1] === 0)) {
    return 0;
  }
  
  const R = 3959; // Earth's radius in miles
  const [lon1, lat1] = coords1;
  const [lon2, lat2] = coords2;
  
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLon = (lon2 - lon1) * Math.PI / 180;
  
  const a = Math.sin(dLat / 2) * Math.sin(dLat / 2) +
            Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
            Math.sin(dLon / 2) * Math.sin(dLon / 2);
  
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  
  return Math.round(R * c);
};

// Stats Summary Component
const StatsSummary = ({ nfts }) => {
  const [showDetails, setShowDetails] = useState(false);
  
  const stats = useMemo(() => {
    const airports = new Map(); // airport -> visit count
    const aircraft = new Map(); // aircraft -> count
    const routes = new Map(); // "ORD-LAX" -> count
    const classBreakdown = new Map(); // class -> count
    let polarisCount = 0;
    let clubCount = 0;
    let totalMiles = 0;

    nfts.forEach(nft => {
      const origin = getAttribute(nft.attributes, 'Origin');
      const dest = getAttribute(nft.attributes, 'Destination');
      const plane = getAttribute(nft.attributes, 'Aircraft');
      const classOfService = getAttribute(nft.attributes, 'Class of Service');
      const hasPolaris = getAttribute(nft.attributes, 'Polaris Lounge') === 'Yes';
      const hasClub = getAttribute(nft.attributes, 'United Club') === 'Yes';

      // Count airports
      if (origin) airports.set(origin, (airports.get(origin) || 0) + 1);
      if (dest) airports.set(dest, (airports.get(dest) || 0) + 1);
      
      // Count aircraft
      if (plane) aircraft.set(plane, (aircraft.get(plane) || 0) + 1);
      
      // Count routes (normalized: always alphabetically sorted)
      if (origin && dest) {
        const routeKey = [origin, dest].sort().join('-');
        routes.set(routeKey, (routes.get(routeKey) || 0) + 1);
        
        // Calculate distance
        const originCoords = getAirport(origin).coords;
        const destCoords = getAirport(dest).coords;
        totalMiles += calculateDistance(originCoords, destCoords);
      }
      
      // Count class of service
      if (classOfService) {
        classBreakdown.set(classOfService, (classBreakdown.get(classOfService) || 0) + 1);
      }
      
      if (hasPolaris) polarisCount++;
      if (hasClub) clubCount++;
    });

    // Get top airports
    const topAirports = [...airports.entries()]
      .sort((a, b) => b[1] - a[1])
      .slice(0, 5);

    // Get top aircraft
    const topAircraft = [...aircraft.entries()]
      .sort((a, b) => b[1] - a[1])
      .slice(0, 3);

    // Get most frequent route
    const topRoute = [...routes.entries()]
      .sort((a, b) => b[1] - a[1])[0];

    // Sort class breakdown by rarity order
    const classOrder = ['Polaris Business', 'United First', 'Premium Plus', 'Economy Plus', 'Economy'];
    const sortedClassBreakdown = classOrder
      .filter(cls => classBreakdown.has(cls))
      .map(cls => ({ class: cls, count: classBreakdown.get(cls) }));

    return {
      totalFlights: nfts.length,
      uniqueAirports: airports.size,
      uniqueAircraft: aircraft.size,
      polarisVisits: polarisCount,
      clubVisits: clubCount,
      totalMiles,
      topAirports,
      topAircraft,
      topRoute,
      classBreakdown: sortedClassBreakdown,
    };
  }, [nfts]);

  // Get class color
  const getClassColor = (className) => {
    const colors = {
      'Polaris Business': '#0033A0',
      'United First': '#60A5FA',
      'Premium Plus': '#9333EA',
      'Economy Plus': '#22C55E',
      'Economy': '#6B7280',
    };
    return colors[className] || '#6B7280';
  };

  const statCardStyle = {
    background: 'rgba(255,255,255,0.03)',
    borderRadius: '10px',
    padding: '16px',
    textAlign: 'center',
    border: '1px solid rgba(255,255,255,0.08)',
  };

  return (
    <div style={{ marginBottom: '24px' }}>
      {/* Main Stats Row */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
        gap: '12px',
        marginBottom: '12px',
      }}>
        {/* Total Flights */}
        <div style={{
          ...statCardStyle,
          background: 'rgba(0, 102, 255, 0.1)',
          border: '1px solid rgba(0, 102, 255, 0.2)',
        }}>
          <div style={{ fontSize: '28px', fontWeight: '700', color: '#0066FF' }}>
            {stats.totalFlights}
          </div>
          <div style={{ fontSize: '11px', color: 'rgba(255,255,255,0.5)', marginTop: '4px' }}>
            Total Flights
          </div>
        </div>

        {/* Total Miles */}
        <div style={{
          ...statCardStyle,
          background: 'rgba(34, 197, 94, 0.1)',
          border: '1px solid rgba(34, 197, 94, 0.2)',
        }}>
          <div style={{ fontSize: '28px', fontWeight: '700', color: '#22C55E' }}>
            {stats.totalMiles.toLocaleString()}
          </div>
          <div style={{ fontSize: '11px', color: 'rgba(255,255,255,0.5)', marginTop: '4px' }}>
            Miles Flown
          </div>
        </div>

        {/* Airports */}
        <div style={statCardStyle}>
          <div style={{ fontSize: '28px', fontWeight: '700', color: '#ffffff' }}>
            {stats.uniqueAirports}
          </div>
          <div style={{ fontSize: '11px', color: 'rgba(255,255,255,0.5)', marginTop: '4px' }}>
            Airports
          </div>
        </div>

        {/* Aircraft Types */}
        <div style={statCardStyle}>
          <div style={{ fontSize: '28px', fontWeight: '700', color: '#ffffff' }}>
            {stats.uniqueAircraft}
          </div>
          <div style={{ fontSize: '11px', color: 'rgba(255,255,255,0.5)', marginTop: '4px' }}>
            Aircraft Types
          </div>
        </div>

        {/* Polaris Visits */}
        <div style={{
          ...statCardStyle,
          background: 'rgba(0, 51, 160, 0.1)',
          border: '1px solid rgba(0, 51, 160, 0.2)',
        }}>
          <div style={{ fontSize: '28px', fontWeight: '700', color: '#0033A0' }}>
            {stats.polarisVisits}
          </div>
          <div style={{ fontSize: '11px', color: 'rgba(255,255,255,0.5)', marginTop: '4px' }}>
            Polaris Visits
          </div>
        </div>

        {/* United Club */}
        <div style={{
          ...statCardStyle,
          background: 'rgba(0, 102, 255, 0.1)',
          border: '1px solid rgba(0, 102, 255, 0.2)',
        }}>
          <div style={{ fontSize: '28px', fontWeight: '700', color: '#0066FF' }}>
            {stats.clubVisits}
          </div>
          <div style={{ fontSize: '11px', color: 'rgba(255,255,255,0.5)', marginTop: '4px' }}>
            Club Visits
          </div>
        </div>
      </div>

      {/* Toggle for detailed stats */}
      <button
        onClick={() => setShowDetails(!showDetails)}
        style={{
          width: '100%',
          padding: '10px',
          background: 'rgba(255,255,255,0.03)',
          border: '1px solid rgba(255,255,255,0.08)',
          borderRadius: '8px',
          color: 'rgba(255,255,255,0.6)',
          cursor: 'pointer',
          fontSize: '12px',
          fontWeight: '500',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '6px',
          transition: 'all 0.2s',
        }}
      >
        {showDetails ? '▲ Hide Stats' : '▼ Show Stats'}
      </button>

      {/* Detailed Stats */}
      {showDetails && (
        <div style={{
          marginTop: '12px',
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
          gap: '12px',
        }}>
          {/* Class of Service Breakdown */}
          <div style={{
            background: 'rgba(255,255,255,0.03)',
            borderRadius: '10px',
            padding: '16px',
            border: '1px solid rgba(255,255,255,0.08)',
          }}>
            <div style={{
              fontSize: '12px',
              fontWeight: '600',
              color: 'rgba(255,255,255,0.7)',
              marginBottom: '12px',
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
            }}>
              <span>🎫</span> Class of Service
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {stats.classBreakdown.map(({ class: cls, count }) => (
                <div key={cls} style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <div style={{
                    width: '10px',
                    height: '10px',
                    borderRadius: '50%',
                    background: getClassColor(cls),
                    flexShrink: 0,
                  }} />
                  <div style={{ flex: 1, fontSize: '12px', color: 'rgba(255,255,255,0.8)' }}>
                    {cls}
                  </div>
                  <div style={{
                    flex: 2,
                    height: '6px',
                    background: 'rgba(255,255,255,0.1)',
                    borderRadius: '3px',
                    overflow: 'hidden',
                  }}>
                    <div style={{
                      height: '100%',
                      width: `${(count / stats.totalFlights) * 100}%`,
                      background: getClassColor(cls),
                      borderRadius: '3px',
                    }} />
                  </div>
                  <div style={{
                    fontSize: '12px',
                    fontWeight: '600',
                    color: getClassColor(cls),
                    minWidth: '24px',
                    textAlign: 'right',
                  }}>
                    {count}
                  </div>
                </div>
              ))}
              {stats.classBreakdown.length === 0 && (
                <div style={{ fontSize: '12px', color: 'rgba(255,255,255,0.4)' }}>
                  No class data available
                </div>
              )}
            </div>
          </div>

          {/* Top Airports */}
          <div style={{
            background: 'rgba(255,255,255,0.03)',
            borderRadius: '10px',
            padding: '16px',
            border: '1px solid rgba(255,255,255,0.08)',
          }}>
            <div style={{
              fontSize: '12px',
              fontWeight: '600',
              color: 'rgba(255,255,255,0.7)',
              marginBottom: '12px',
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
            }}>
              <span>🛫</span> Top Airports
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
              {stats.topAirports.map(([code, count], i) => {
                const airport = getAirport(code);
                return (
                  <div key={code} style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '10px',
                    padding: '6px 0',
                    borderBottom: i < stats.topAirports.length - 1 ? '1px solid rgba(255,255,255,0.05)' : 'none',
                  }}>
                    <div style={{
                      fontSize: '14px',
                      fontWeight: '700',
                      color: '#0066FF',
                      minWidth: '40px',
                    }}>
                      {code}
                    </div>
                    <div style={{
                      flex: 1,
                      fontSize: '11px',
                      color: 'rgba(255,255,255,0.5)',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}>
                      {airport.city}
                    </div>
                    <div style={{
                      fontSize: '12px',
                      fontWeight: '600',
                      color: 'rgba(255,255,255,0.8)',
                    }}>
                      {count}x
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Top Aircraft & Route */}
          <div style={{
            background: 'rgba(255,255,255,0.03)',
            borderRadius: '10px',
            padding: '16px',
            border: '1px solid rgba(255,255,255,0.08)',
          }}>
            {/* Top Aircraft */}
            <div style={{
              fontSize: '12px',
              fontWeight: '600',
              color: 'rgba(255,255,255,0.7)',
              marginBottom: '12px',
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
            }}>
              <span>✈️</span> Top Aircraft
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', marginBottom: '16px' }}>
              {stats.topAircraft.map(([aircraft, count], i) => (
                <div key={aircraft} style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  padding: '6px 0',
                  borderBottom: i < stats.topAircraft.length - 1 ? '1px solid rgba(255,255,255,0.05)' : 'none',
                }}>
                  <div style={{ fontSize: '12px', color: 'rgba(255,255,255,0.8)' }}>
                    {aircraft}
                  </div>
                  <div style={{
                    fontSize: '12px',
                    fontWeight: '600',
                    color: 'rgba(255,255,255,0.8)',
                  }}>
                    {count}x
                  </div>
                </div>
              ))}
            </div>

            {/* Most Frequent Route */}
            {stats.topRoute && (
              <>
                <div style={{
                  fontSize: '12px',
                  fontWeight: '600',
                  color: 'rgba(255,255,255,0.7)',
                  marginBottom: '8px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                }}>
                  <span>🔄</span> Most Frequent Route
                </div>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '12px',
                  padding: '12px',
                  background: 'rgba(0, 102, 255, 0.1)',
                  borderRadius: '8px',
                }}>
                  <span style={{ fontSize: '18px', fontWeight: '700', color: '#0066FF' }}>
                    {stats.topRoute[0].split('-')[0]}
                  </span>
                  <span style={{ color: 'rgba(255,255,255,0.4)' }}>⟷</span>
                  <span style={{ fontSize: '18px', fontWeight: '700', color: '#0066FF' }}>
                    {stats.topRoute[0].split('-')[1]}
                  </span>
                  <span style={{
                    fontSize: '12px',
                    color: 'rgba(255,255,255,0.6)',
                    marginLeft: '8px',
                  }}>
                    ({stats.topRoute[1]}x)
                  </span>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

// Main Gallery Component
const Gallery = ({ connectedAccount }) => {
  const { nfts, loading, error, totalSupply, fetchNFTs, fetchNFTsByOwner } = useGallery();
  const [viewMode, setViewMode] = useState('all'); // 'all' or 'mine'
  const [filters, setFilters] = useState(DEFAULT_FILTERS);
  const [showFilters, setShowFilters] = useState(true);

  useEffect(() => {
    console.log('Gallery useEffect - viewMode:', viewMode, 'connectedAccount:', connectedAccount);
    if (viewMode === 'all') {
      fetchNFTs(20);
    } else if (viewMode === 'mine' && connectedAccount) {
      console.log('Fetching NFTs for owner:', connectedAccount);
      fetchNFTsByOwner(connectedAccount);
    } else if (viewMode === 'mine' && !connectedAccount) {
      console.warn('My Flights view selected but no wallet connected');
    }
  }, [viewMode, connectedAccount, fetchNFTs, fetchNFTsByOwner]);

  // Filter NFTs based on current filters
  const filteredNfts = useMemo(() => {
    if (viewMode !== 'mine') return nfts;

    return nfts.filter(nft => {
      const flightNumber = getAttribute(nft.attributes, 'Flight Number');
      const date = getAttribute(nft.attributes, 'Date');
      const origin = getAttribute(nft.attributes, 'Origin');
      const destination = getAttribute(nft.attributes, 'Destination');
      const aircraft = getAttribute(nft.attributes, 'Aircraft');
      const tailNumber = getAttribute(nft.attributes, 'Tail Number');
      const configuration = getAttribute(nft.attributes, 'Seat Configuration');
      const classOfService = getAttribute(nft.attributes, 'Class of Service');
      const hasPolarisLounge = getAttribute(nft.attributes, 'Polaris Lounge') === 'Yes';
      const hasUnitedClub = getAttribute(nft.attributes, 'United Club') === 'Yes';

      // Check each filter
      if (filters.flightNumber && !flightNumber.toLowerCase().includes(filters.flightNumber.toLowerCase())) {
        return false;
      }
      if (filters.date && date !== filters.date) {
        return false;
      }
      if (filters.departure && origin !== filters.departure) {
        return false;
      }
      if (filters.arrival && destination !== filters.arrival) {
        return false;
      }
      if (filters.aircraft && aircraft !== filters.aircraft) {
        return false;
      }
      if (filters.tailNumber && tailNumber !== filters.tailNumber) {
        return false;
      }
      if (filters.configuration && configuration !== filters.configuration) {
        return false;
      }
      if (filters.classOfService && classOfService !== filters.classOfService) {
        return false;
      }
      if (filters.hasPolarisLounge && !hasPolarisLounge) {
        return false;
      }
      if (filters.hasUnitedClub && !hasUnitedClub) {
        return false;
      }

      return true;
    });
  }, [nfts, filters, viewMode]);

  const clearFilters = () => {
    setFilters(DEFAULT_FILTERS);
  };

  return (
    <div>
      {/* Header */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '24px',
        flexWrap: 'wrap',
        gap: '16px',
      }}>
        <div>
          <h2 style={{
            fontSize: '20px',
            fontWeight: '600',
            color: '#ffffff',
            margin: '0 0 4px 0',
            display: 'flex',
            alignItems: 'center',
            gap: '10px',
          }}>
            <span style={{ fontSize: '22px' }}>🖼️</span>
            NFT Gallery
          </h2>
          <div style={{
            fontSize: '13px',
            color: 'rgba(255,255,255,0.5)',
          }}>
            {viewMode === 'mine' 
              ? `${filteredNfts.length} of ${nfts.length} flight${nfts.length !== 1 ? 's' : ''} shown`
              : `${totalSupply} flight${totalSupply !== 1 ? 's' : ''} minted`
            }
          </div>
        </div>

        <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
          {/* Filter toggle (only in My Flights) */}
          {viewMode === 'mine' && nfts.length > 0 && (
            <button
              onClick={() => setShowFilters(!showFilters)}
              style={{
                padding: '8px 16px',
                borderRadius: '8px',
                border: '1px solid rgba(255,255,255,0.12)',
                background: showFilters ? 'rgba(0, 102, 255, 0.2)' : 'rgba(255,255,255,0.05)',
                color: showFilters ? '#60a5fa' : 'rgba(255,255,255,0.7)',
                cursor: 'pointer',
                fontSize: '13px',
                fontWeight: '500',
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
              }}
            >
              <span>🔍</span>
              {showFilters ? 'Hide Filters' : 'Show Filters'}
            </button>
          )}

          {/* View toggle */}
          <div style={{
            display: 'flex',
            gap: '8px',
            background: 'rgba(255,255,255,0.05)',
            padding: '4px',
            borderRadius: '8px',
          }}>
            <button
              onClick={() => setViewMode('all')}
              style={{
                padding: '8px 16px',
                borderRadius: '6px',
                border: 'none',
                background: viewMode === 'all' ? 'rgba(0, 102, 255, 0.2)' : 'transparent',
                color: viewMode === 'all' ? '#60a5fa' : 'rgba(255,255,255,0.5)',
                cursor: 'pointer',
                fontSize: '13px',
                fontWeight: '500',
                transition: 'all 0.2s',
              }}
            >
              All Flights
            </button>
            <button
              onClick={() => setViewMode('mine')}
              disabled={!connectedAccount}
              style={{
                padding: '8px 16px',
                borderRadius: '6px',
                border: 'none',
                background: viewMode === 'mine' ? 'rgba(0, 102, 255, 0.2)' : 'transparent',
                color: viewMode === 'mine' ? '#60a5fa' : 'rgba(255,255,255,0.5)',
                cursor: connectedAccount ? 'pointer' : 'not-allowed',
                fontSize: '13px',
                fontWeight: '500',
                transition: 'all 0.2s',
                opacity: connectedAccount ? 1 : 0.5,
              }}
            >
              My Flights
            </button>
          </div>
        </div>
      </div>

      {/* Stats Summary (only in My Flights view with NFTs) */}
      {viewMode === 'mine' && nfts.length > 0 && !loading && (
        <StatsSummary nfts={nfts} />
      )}

      {/* Filter Panel (only in My Flights view) */}
      {viewMode === 'mine' && showFilters && nfts.length > 0 && !loading && (
        <FilterPanel
          filters={filters}
          setFilters={setFilters}
          nfts={nfts}
          onClear={clearFilters}
        />
      )}

      {/* Loading state */}
      {loading && (
        <div style={{
          textAlign: 'center',
          padding: '60px 20px',
          color: 'rgba(255,255,255,0.5)',
        }}>
          <div style={{ fontSize: '32px', marginBottom: '16px' }}>✈️</div>
          <div>Loading flights...</div>
        </div>
      )}

      {/* Error state */}
      {error && (
        <div style={{
          textAlign: 'center',
          padding: '60px 20px',
          color: '#ef4444',
          background: 'rgba(239, 68, 68, 0.1)',
          borderRadius: '12px',
          border: '1px solid rgba(239, 68, 68, 0.2)',
        }}>
          <div style={{ marginBottom: '8px' }}>Failed to load NFTs</div>
          <div style={{ fontSize: '13px', opacity: 0.8 }}>{error}</div>
        </div>
      )}

      {/* Empty state */}
      {!loading && !error && nfts.length === 0 && (
        <div style={{
          textAlign: 'center',
          padding: '60px 20px',
          color: 'rgba(255,255,255,0.5)',
          background: 'rgba(255,255,255,0.03)',
          borderRadius: '12px',
          border: '1px solid rgba(255,255,255,0.08)',
        }}>
          <div style={{ fontSize: '48px', marginBottom: '16px' }}>🛫</div>
          <div style={{ fontSize: '16px', marginBottom: '8px', color: '#ffffff' }}>
            {viewMode === 'mine' ? 'No flights in your collection' : 'No flights minted yet'}
          </div>
          <div style={{ fontSize: '13px' }}>
            {viewMode === 'mine' 
              ? 'Mint your first flight to start your collection!' 
              : 'Be the first to mint a flight segment NFT!'}
          </div>
        </div>
      )}

      {/* No results after filtering */}
      {!loading && !error && viewMode === 'mine' && nfts.length > 0 && filteredNfts.length === 0 && (
        <div style={{
          textAlign: 'center',
          padding: '60px 20px',
          color: 'rgba(255,255,255,0.5)',
          background: 'rgba(255,255,255,0.03)',
          borderRadius: '12px',
          border: '1px solid rgba(255,255,255,0.08)',
        }}>
          <div style={{ fontSize: '48px', marginBottom: '16px' }}>🔍</div>
          <div style={{ fontSize: '16px', marginBottom: '8px', color: '#ffffff' }}>
            No flights match your filters
          </div>
          <div style={{ fontSize: '13px', marginBottom: '16px' }}>
            Try adjusting your filter criteria
          </div>
          <button
            onClick={clearFilters}
            style={{
              padding: '10px 20px',
              borderRadius: '8px',
              border: 'none',
              background: 'rgba(0, 102, 255, 0.2)',
              color: '#60a5fa',
              cursor: 'pointer',
              fontSize: '13px',
              fontWeight: '500',
            }}
          >
            Clear All Filters
          </button>
        </div>
      )}

      {/* NFT Grid */}
      {!loading && !error && filteredNfts.length > 0 && (
        <div className="gallery-grid" style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
          gap: '20px',
        }}>
          {filteredNfts.map((nft) => (
            <NFTCard
              key={nft.tokenId}
              nft={nft}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default Gallery;
